import {
  FieldValues,
  FormProvider,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Form, NavLink, useLoaderData, useNavigate } from 'react-router-dom';

import AvatarInput from '../../../components/shared/AvatarInput';
import Button from '../../../components/shared/Button';
import FakeInput from '../../../components/shared/Input/FakeInput';
import { FakeCheckbox } from '../../../components/shared/CheckboxInput';
import Input from '../../../components/shared/Input';
import LanguageDropdown from '../../../components/shared/DropdownSelect/LanguageDropdown';
import ToggleSwitchControlled from '../../../components/shared/ToggleSwitch/ToggleSwitchControlled';

import {
  REQUIRED_VALIDATION_RULE,
  RESET_PASSWORD_PATH,
} from '../../../constants';
import { ActionResponse } from '../../../types';
import {
  extractMultipleErrors,
  hasNestedObjects,
  showToast,
} from '../../../utils';

import ForumMembersStore from '../services/forumMembers.store';
const forumMembersStore = new ForumMembersStore();

const MyProfileForm = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const loaderData = useLoaderData() as ActionResponse<any> | null;
  const data = loaderData?.success ? loaderData.data : undefined;

  const lastName = data?.last_name || '';
  const mobilePhone = data?.mobile_phone || '';
  const regionsRolesList = data?.region_roles_list || [];

  const methods = useForm<any>({
    defaultValues: {
      avatar: '',
      fraudCaseNotification: data?.fraud_case_notification === 0 ? false : true,
      fraudCaseCommentNotification:
        data?.fraud_case_comment_notification === 0 ? false : true,
      lastName: lastName,
      mobilePhone: mobilePhone,
    },
  });

  const {
    handleSubmit,
    setError,
    formState: { isSubmitting },
  } = methods;

  /* Handle Submit */
  const onSubmit: SubmitHandler<FieldValues> = async (fieldValues) => {
    const handleError = (message: any) => {
      if (hasNestedObjects(message)) {
        const errorMessages = extractMultipleErrors(message);
        for (const key in errorMessages) {
          setError(key as any, {
            type: 'backend',
            message: errorMessages[key],
          });
        }
      } else {
        showToast(message, false, true);
      }
    };

    let formData = new FormData();

    Object.keys(fieldValues).forEach((key) => {
      if (
        !['fraudCaseNotification', 'fraudCaseCommentNotification'].includes(key)
      ) {
        formData.append(key, fieldValues[key]);
      }
    });

    // notifications
    formData.append(
      'fraudCaseNotification',
      fieldValues.fraudCaseNotification ? '1' : '0'
    );
    formData.append(
      'fraudCaseCommentNotification',
      fieldValues.fraudCaseCommentNotification ? '1' : '0'
    );

    const response = await forumMembersStore.updateForumMember({
      id: data.id,
      payload: formData,
      isProfile: true,
    });

    const { success, message } = response;

    if (success) {
      showToast(message, success);
      navigate('/');
    } else {
      // TODO check again because its hardcoded...
      if (message === 'Account with email already exists.') {
        setError('email', {
          type: 'manual',
          message: t('forum_members.member_with_email_exists'),
        });
      }
      handleError(message);
    }
  };

  return (
    <FormProvider {...methods}>
      <Form
        method="post"
        className="pks-layout-col-xl"
        onSubmit={handleSubmit(onSubmit)}
      >
        {/* Avatar Upload */}
        <AvatarInput
          userId={data?.id}
          userName={`${data?.first_name || ''} ${data?.last_name || ''}`}
          userFunction={data?.organization_function}
          hasAvatar={data?.avatar ? true : false}
        />

        {/* Notifications Toggle */}
        <div className="space-y-4 md:mb-10">
          {/* Profile Page - Reset Password Link */}
          <NavLink to={RESET_PASSWORD_PATH} className="underline">
            {t('auth.change_password_link')}
          </NavLink>

          <div>
            <ToggleSwitchControlled
              id="fraudCaseNotification"
              name="fraudCaseNotification"
              label={t('fraud_cases.fraud_cases_email_notifications')}
              isTitle={false}
              showToggle={true}
            />
            <ToggleSwitchControlled
              id="fraudCaseCommentNotification"
              name="fraudCaseCommentNotification"
              label={t('fraud_cases.fraud_cases_comment_notifications')}
              isTitle={false}
              showToggle={true}
            />

            <div className="text-sm italic">
              *{t('forum_members.prompt_forum_member_save')}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-8 md:flex-row">
          {/* User Details */}
          <div className="pks-layout-col flex-1">
            {/* First and Last Name */}
            <div className="flex flex-col gap-2 sm:flex-row">
              {/* First Name */}
              {data?.first_name && (
                <div className="flex-1">
                  <FakeInput id="name" label={`${t('inputs.name.label')}*`}>
                    {data.first_name}
                  </FakeInput>
                </div>
              )}

              {/* Last Name Input Field */}
              <div className="flex-1">
                <Input
                  type="text"
                  id="lastName"
                  name="lastName"
                  label={`${t('inputs.surname.label')}*`}
                  placeholder={t('inputs.surname.placeholder')}
                  autoComplete="off"
                  validationRules={REQUIRED_VALIDATION_RULE}
                />
              </div>
            </div>

            {/* Email */}
            {data?.email && (
              <FakeInput id="name" label={`${t('inputs.email.label')}*`}>
                {data.email}
              </FakeInput>
            )}

            {/* Phone */}
            {data?.phone && (
              <FakeInput id="name" label={t('inputs.phone.label')}>
                {data.phone}
              </FakeInput>
            )}

            {/* Mobile Phone Input Field */}
            <Input
              type="text"
              id="mobilePhone"
              name="mobilePhone"
              label={`${t('inputs.mobile_phone.label')}*`}
              placeholder={t('inputs.mobile_phone.placeholder')}
              autoComplete="off"
              validationRules={REQUIRED_VALIDATION_RULE}
              numeric
            />

            {/* Organization */}
            {data?.organization?.name && (
              <FakeInput
                id="organizationName"
                label={`${t('institutions.institution')}*`}
              >
                {data.organization.name}
              </FakeInput>
            )}

            {/* Organization Function */}
            {data?.organization_function && (
              <FakeInput id="function" label={`${t('inputs.function.label')}*`}>
                {data.organization_function}
              </FakeInput>
            )}

            {/* Language Dropdown */}
            <>
              <LanguageDropdown />
              <div className="text-sm italic">
                *{t('forum_members.prompt_forum_member_save')}
              </div>
            </>
          </div>
          {/* Regions and Roles */}
          <div className="relative pks-layout-col flex-1 border-t border-t-secondary-300 pt-4 md:pt-0 md:border-none">
            {/* All Regions */}
            {data?.regions_list?.allRegions === 1 && (
              <div className="md:-mt-7">
                <FakeCheckbox label={t('regions.all_regions')} checked={true} />
              </div>
            )}

            {/* Regions and Roles */}
            {regionsRolesList?.length > 0 &&
              regionsRolesList.map((regionRole: any, index: number) => (
                <div key={index} className="pks-layout-col">
                  {/* Render your content here */}
                  {regionRole?.regionId && (
                    <FakeInput
                      id={`role-${regionRole.regionId}`}
                      label={`${
                        index !== 0
                          ? `${t('regions.region')} ${index + 1}`
                          : t('regions.region')
                      }*`}
                    >
                      {regionRole?.name}
                    </FakeInput>
                  )}
                  {regionRole?.roles.length > 0 && (
                    <FakeInput
                      id="roles-input"
                      label={`${
                        index !== 0
                          ? `${t('roles.role')} ${index + 1}`
                          : `${t('roles.role')}`
                      }*`} // No need to number it, as it's a single input
                    >
                      {regionRole.roles
                        .filter((role: any) => role?.id) // Ensure only valid roles are included
                        .map((role: any) => role.name) // Extract names
                        .join(', ')}{' '}
                    </FakeInput>
                  )}
                </div>
              ))}
          </div>
        </div>

        {/* Form Actions */}
        <div className="w-full inline-flex gap-4 flex-wrap sm:flex-nowrap">
          {/* Submit Button */}
          <Button
            type="submit"
            className="flex-grow"
            wide
            isDisabled={isSubmitting}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? t('state.submitting')
              : t('forum_members.edit_profile')}
          </Button>

          {/* Back Button */}
          <span className="flex-auto sm:flex-none focus:outline-none group">
            <Button
              onClick={() => navigate('/')}
              type="button"
              variant="secondary"
              wide
              tabIndex={-1}
            >
              {t('global.cancel')}
            </Button>
          </span>
        </div>
      </Form>
    </FormProvider>
  );
};

export default MyProfileForm;

export async function loader(): Promise<ActionResponse<any> | null> {
  const data = await forumMembersStore.preloadForumMember();
  return data;
}
